
import { useState, useEffect } from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Badge } from '@/components/ui/badge'
import { X } from 'lucide-react'
import { useCreateOrUpdateUserDietProfile, useUserDietProfile } from '@/api/user-diet-profiles'
import { calculateBMI, calculateBMR, calculateTDEE } from '@/lib/diet-calculations'
import type { CreateUserDietProfileData } from '@/types/diet-v2'

interface UserDietProfileFormProps {
  userId: string
  onSuccess?: () => void
}

const commonAllergens = ['dairy', 'nuts', 'gluten', 'shellfish', 'eggs', 'soy', 'fish', 'peanuts']

export function UserDietProfileForm({ userId, onSuccess }: UserDietProfileFormProps) {
  const { data: existingProfile } = useUserDietProfile(userId)
  const mutation = useCreateOrUpdateUserDietProfile()

  const [formData, setFormData] = useState<Omit<CreateUserDietProfileData, 'userId'>>({
    currentWeight: 70,
    targetWeight: 65,
    targetWeeks: 12,
    goalType: 'weight-loss',
    age: 30,
    height: 170,
    heightUnit: 'cm',
    gender: 'male',
    activityLevel: 'moderate',
    allergens: [],
    nonPreferredFoods: [],
    dietaryRestriction: 'none',
  })

  const [newAllergen, setNewAllergen] = useState('')
  const [newNonPreferredFood, setNewNonPreferredFood] = useState('')

  // Load existing profile
  useEffect(() => {
    if (existingProfile) {
      setFormData({
        currentWeight: existingProfile.currentWeight,
        targetWeight: existingProfile.targetWeight,
        targetWeeks: existingProfile.targetWeeks,
        goalType: existingProfile.goalType,
        age: existingProfile.age,
        height: existingProfile.height,
        heightUnit: existingProfile.heightUnit,
        gender: existingProfile.gender,
        activityLevel: existingProfile.activityLevel,
        allergens: existingProfile.allergens,
        nonPreferredFoods: existingProfile.nonPreferredFoods,
        dietaryRestriction: existingProfile.dietaryRestriction,
      })
    }
  }, [existingProfile])

  // Calculate metrics
  const height = formData.heightUnit === 'cm' ? formData.height : formData.height * 2.54
  const bmi = calculateBMI(formData.currentWeight, height, 'cm')
  const bmr = calculateBMR(formData.currentWeight, height, formData.age, formData.gender)
  const tdee = calculateTDEE(bmr, formData.activityLevel)
  const weeklyWeightChange = (formData.currentWeight - formData.targetWeight) / formData.targetWeeks
  const dailyCalorieDeficit = weeklyWeightChange * 7700 / 7 // 7700 calories per kg

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    try {
      await mutation.mutateAsync({ ...formData, userId })
      if (onSuccess) onSuccess()
    } catch (error) {
      // Error handled by mutation
    }
  }

  const addAllergen = (allergen: string) => {
    if (allergen && !formData.allergens.includes(allergen)) {
      setFormData({ ...formData, allergens: [...formData.allergens, allergen] })
      setNewAllergen('')
    }
  }

  const removeAllergen = (allergen: string) => {
    setFormData({ ...formData, allergens: formData.allergens.filter((a) => a !== allergen) })
  }

  const addNonPreferredFood = () => {
    if (newNonPreferredFood && !formData.nonPreferredFoods.includes(newNonPreferredFood)) {
      setFormData({ ...formData, nonPreferredFoods: [...formData.nonPreferredFoods, newNonPreferredFood] })
      setNewNonPreferredFood('')
    }
  }

  const removeNonPreferredFood = (food: string) => {
    setFormData({ ...formData, nonPreferredFoods: formData.nonPreferredFoods.filter((f) => f !== food) })
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Goals Section */}
      <Card>
        <CardHeader>
          <CardTitle>Goals</CardTitle>
          <CardDescription>Set your weight and fitness goals</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="goalType">Goal Type</Label>
            <Select value={formData.goalType} onValueChange={(v: any) => setFormData({ ...formData, goalType: v })}>
              <SelectTrigger id="goalType">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="weight-loss">Weight Loss</SelectItem>
                <SelectItem value="muscle-gain">Muscle Gain</SelectItem>
                <SelectItem value="maintenance">Maintenance</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label htmlFor="currentWeight">Current Weight (kg)</Label>
              <Input
                id="currentWeight"
                type="number"
                step="0.1"
                value={formData.currentWeight}
                onChange={(e) => setFormData({ ...formData, currentWeight: parseFloat(e.target.value) })}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="targetWeight">Target Weight (kg)</Label>
              <Input
                id="targetWeight"
                type="number"
                step="0.1"
                value={formData.targetWeight}
                onChange={(e) => setFormData({ ...formData, targetWeight: parseFloat(e.target.value) })}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="targetWeeks">Timeline (weeks)</Label>
              <Input
                id="targetWeeks"
                type="number"
                value={formData.targetWeeks}
                onChange={(e) => setFormData({ ...formData, targetWeeks: parseInt(e.target.value) })}
                required
              />
            </div>
          </div>

          <div className="bg-muted p-4 rounded-lg">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-muted-foreground">Weekly Change:</span>
                <span className="font-medium ml-2">{Math.abs(weeklyWeightChange).toFixed(2)} kg/week</span>
              </div>
              <div>
                <span className="text-muted-foreground">Daily Calorie Adjustment:</span>
                <span className="font-medium ml-2">{Math.round(dailyCalorieDeficit)} cal</span>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Physical Attributes */}
      <Card>
        <CardHeader>
          <CardTitle>Physical Attributes</CardTitle>
          <CardDescription>Your body measurements and activity level</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="age">Age</Label>
              <Input
                id="age"
                type="number"
                value={formData.age}
                onChange={(e) => setFormData({ ...formData, age: parseInt(e.target.value) })}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="gender">Gender</Label>
              <Select value={formData.gender} onValueChange={(v: any) => setFormData({ ...formData, gender: v })}>
                <SelectTrigger id="gender">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="male">Male</SelectItem>
                  <SelectItem value="female">Female</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="height">Height</Label>
              <Input
                id="height"
                type="number"
                step="0.1"
                value={formData.height}
                onChange={(e) => setFormData({ ...formData, height: parseFloat(e.target.value) })}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="heightUnit">Unit</Label>
              <Select value={formData.heightUnit} onValueChange={(v: any) => setFormData({ ...formData, heightUnit: v })}>
                <SelectTrigger id="heightUnit">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="cm">Centimeters (cm)</SelectItem>
                  <SelectItem value="in">Inches (in)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="activityLevel">Activity Level</Label>
            <Select value={formData.activityLevel} onValueChange={(v: any) => setFormData({ ...formData, activityLevel: v })}>
              <SelectTrigger id="activityLevel">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="sedentary">Sedentary (little or no exercise)</SelectItem>
                <SelectItem value="light">Light (exercise 1-3 days/week)</SelectItem>
                <SelectItem value="moderate">Moderate (exercise 3-5 days/week)</SelectItem>
                <SelectItem value="active">Active (exercise 6-7 days/week)</SelectItem>
                <SelectItem value="very-active">Very Active (intense exercise daily)</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="bg-muted p-4 rounded-lg space-y-2">
            <div className="grid grid-cols-3 gap-4 text-sm">
              <div>
                <span className="text-muted-foreground">BMI:</span>
                <span className="font-medium ml-2">{bmi}</span>
              </div>
              <div>
                <span className="text-muted-foreground">BMR:</span>
                <span className="font-medium ml-2">{bmr} cal</span>
              </div>
              <div>
                <span className="text-muted-foreground">TDEE:</span>
                <span className="font-medium ml-2">{tdee} cal</span>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Dietary Preferences */}
      <Card>
        <CardHeader>
          <CardTitle>Dietary Preferences</CardTitle>
          <CardDescription>Allergens, restrictions, and food preferences</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="dietaryRestriction">Dietary Restriction</Label>
            <Select
              value={formData.dietaryRestriction}
              onValueChange={(v: any) => setFormData({ ...formData, dietaryRestriction: v })}
            >
              <SelectTrigger id="dietaryRestriction">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">None</SelectItem>
                <SelectItem value="vegetarian">Vegetarian</SelectItem>
                <SelectItem value="vegan">Vegan</SelectItem>
                <SelectItem value="keto">Keto</SelectItem>
                <SelectItem value="paleo">Paleo</SelectItem>
                <SelectItem value="halal">Halal</SelectItem>
                <SelectItem value="kosher">Kosher</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>Allergens</Label>
            <div className="flex flex-wrap gap-2 mb-2">
              {commonAllergens.map((allergen) => (
                <Badge
                  key={allergen}
                  variant={formData.allergens.includes(allergen) ? 'default' : 'outline'}
                  className="cursor-pointer"
                  onClick={() => {
                    if (formData.allergens.includes(allergen)) {
                      removeAllergen(allergen)
                    } else {
                      addAllergen(allergen)
                    }
                  }}
                >
                  {allergen}
                </Badge>
              ))}
            </div>
            <div className="flex gap-2">
              <Input
                value={newAllergen}
                onChange={(e) => setNewAllergen(e.target.value)}
                placeholder="Add custom allergen..."
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault()
                    addAllergen(newAllergen)
                  }
                }}
              />
              <Button type="button" onClick={() => addAllergen(newAllergen)}>
                Add
              </Button>
            </div>
            {formData.allergens.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-2">
                {formData.allergens.map((allergen) => (
                  <Badge key={allergen} variant="destructive">
                    {allergen}
                    <X className="h-3 w-3 ml-1 cursor-pointer" onClick={() => removeAllergen(allergen)} />
                  </Badge>
                ))}
              </div>
            )}
          </div>

          <div className="space-y-2">
            <Label>Non-Preferred Foods</Label>
            <div className="flex gap-2">
              <Input
                value={newNonPreferredFood}
                onChange={(e) => setNewNonPreferredFood(e.target.value)}
                placeholder="e.g., mushrooms, fish..."
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault()
                    addNonPreferredFood()
                  }
                }}
              />
              <Button type="button" onClick={addNonPreferredFood}>
                Add
              </Button>
            </div>
            {formData.nonPreferredFoods.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-2">
                {formData.nonPreferredFoods.map((food) => (
                  <Badge key={food} variant="secondary">
                    {food}
                    <X className="h-3 w-3 ml-1 cursor-pointer" onClick={() => removeNonPreferredFood(food)} />
                  </Badge>
                ))}
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      <div className="flex justify-end gap-4">
        <Button type="submit" disabled={mutation.isPending}>
          {mutation.isPending ? 'Saving...' : existingProfile ? 'Update Profile' : 'Create Profile'}
        </Button>
      </div>
    </form>
  )
}
